import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, CloudUpload, FileSpreadsheet, Fingerprint, Images, ShieldCheck, Sparkles, Users, Zap } from 'lucide-react';
import { AuroraBackdrop } from '../components/AuroraBackdrop';
import { BrandMark } from '../components/BrandMark';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
const features = [{
  icon: CloudUpload,
  title: 'Live Firestore records',
  body: 'Every student you record is written straight to Cloud Firestore and streams back in real time.'
}, {
  icon: Images,
  title: 'Photos in Firebase Storage',
  body: 'Upload a portrait per student to the student_photos bucket with instant preview and thumbnails.'
}, {
  icon: FileSpreadsheet,
  title: 'Formatted Excel reports',
  body: 'Export all students, or filter by class or gender, into a clean, bordered .xlsx workbook.'
}, {
  icon: Fingerprint,
  title: 'Two-layer security',
  body: 'Google OAuth sign-in, then your own master password gate before the dashboard opens.'
}];
const floaters = [{
  icon: Users,
  className: 'left-[6%] top-[22%]',
  delay: 0
}, {
  icon: FileSpreadsheet,
  className: 'right-[8%] top-[18%]',
  delay: 0.6
}, {
  icon: ShieldCheck,
  className: 'left-[12%] bottom-[16%]',
  delay: 1.1
}, {
  icon: Zap,
  className: 'right-[12%] bottom-[22%]',
  delay: 1.6
}];
export function Landing() {
  const navigate = useNavigate();
  const {
    user,
    unlocked,
    signInError
  } = useAuth();
  const goNext = () => navigate(user ? unlocked ? '/dashboard' : '/security' : '/security');
  return <div className="relative min-h-screen w-full overflow-hidden" data-id="element-534" data-mp="14p0vob6thyyv">
      <AuroraBackdrop intensity="marketing" data-id="element-535" data-mp="14qbyo8sfv2ox" />

      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6" data-id="element-536" data-mp="zwr8v4101f0t">
        <BrandMark size="sm" data-id="element-537" data-mp="1itrgcdwcs0fy" />
        <div className="flex items-center gap-2" data-id="element-538" data-mp="1iut1jb7323be">
          <ThemeToggle data-id="element-539" data-mp="1ntnis8pejjte" />
          {user ? <button type="button" onClick={goNext} className="glass-flat inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-sm font-medium text-foreground" data-id="element-540" data-mp="1nscfe9zvce2t">
              Continue
              <ArrowRight className="h-3.5 w-3.5" data-id="element-541" data-mp="103t4rntcjp7k" />
            </button> : null}
        </div>
      </header>

      {/* Floating decorations */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block" data-id="element-542" data-mp="1547d76yv1164">
        {floaters.map((floater, index) => <motion.span key={index} animate={{
        y: [0, -18, 0]
      }} transition={{
        duration: 6 + index,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: floater.delay
      }} className={`glass absolute grid h-14 w-14 place-items-center rounded-2xl text-brand ${floater.className}`} data-id="element-543" data-mp="v46t3it62h0v" data-mpi={index}>
            <floater.icon className="h-6 w-6" strokeWidth={1.7} data-id="element-544" data-mp="dw5xly2a6s8t" />
          </motion.span>)}
      </div>

      <main className="relative mx-auto w-full max-w-6xl px-5 pb-24" data-id="element-545" data-mp="9uu25bwq7csp">
        <section className="flex flex-col items-center pt-10 text-center sm:pt-16" data-id="element-546" data-mp="nlh55g9iroul">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="glass-flat inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium text-muted-foreground" data-id="element-547" data-mp="iuh7g8skt2r1">
            <Sparkles className="h-3.5 w-3.5 text-brand" data-id="element-548" data-mp="16jjlz7rwyfhb" />
            Built on real Firebase — no demo data, ever
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.06
        }} className="mt-6 max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-6xl" data-id="element-549" data-mp="11m08ia31i6qf">
            The calm way to record <span className="gradient-text" data-id="element-550" data-mp="35qguzw0nwfoh">every student</span> in your school
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.12
        }} className="mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg" data-id="element-551" data-mp="39qimxdglhuqk">
            Student Recorder keeps names, classes, parents, telephone numbers, photos and notes in one live database —
            searchable in seconds and exportable to a polished Excel report whenever you need it.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.18
        }} className="mt-9 flex flex-col items-center gap-3 sm:flex-row" data-id="element-552" data-mp="2vzm6ekzy13zv">
            {user ? <button type="button" onClick={goNext} className="inline-flex h-12 items-center gap-2 rounded-2xl brand-gradient px-7 text-sm font-semibold text-white shadow-xl shadow-indigo-500/30 transition-transform hover:-translate-y-0.5" data-id="element-553" data-mp="28a1n20zvo4q2">
                Get Started
                <ArrowRight className="h-4 w-4" data-id="element-554" data-mp="1qvqml6djctvs" />
              </button> : <GoogleSignInButton label="Get Started with Google" onSignedIn={() => navigate('/security')} data-id="element-555" data-mp="1lub51oakh5i5" />}
            <p className="text-xs text-muted-foreground" data-id="element-556" data-mp="1husfgf7sw68z">Google OAuth + your own master password</p>
          </motion.div>

          {signInError && <div role="alert" className="mt-5 flex max-w-xl items-start gap-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-left text-sm" data-id="element-557" data-mp="183xurfa7depn">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" data-id="element-558" data-mp="y3owzx0kbl85" />
              <p className="text-muted-foreground" data-id="element-559" data-mp="u4g1amlypd9y">{signInError}</p>
            </div>}
        </section>

        <motion.section initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.24
      }} className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Product highlights" data-id="element-560" data-mp="jhi33iev4ymc">
          {features.map(feature => <article key={feature.title} className="glass rounded-3xl p-5 text-left" data-id="element-561" data-mp="fisfkx0420oz">
              <span className="grid h-11 w-11 place-items-center rounded-2xl brand-gradient-soft text-brand" data-id="element-562" data-mp="136se4j3nyve4">
                <feature.icon className="h-5 w-5" strokeWidth={1.8} data-id="element-563" data-mp="179ykdqqs0mkl" />
              </span>
              <h2 className="mt-4 text-sm font-semibold tracking-tight text-foreground" data-id="element-564" data-mp="tg78wcthism8">{feature.title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground" data-id="element-565" data-mp="ydftek23v8qn">{feature.body}</p>
            </article>)}
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="glass mt-6 flex flex-col items-center gap-6 rounded-3xl p-7 text-center sm:flex-row sm:justify-between sm:p-9 sm:text-left" data-id="element-566" data-mp="1qw3dbw5d8hq5">
          <div className="max-w-xl" data-id="element-567" data-mp="29z55i2xq4hma">
            <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl" data-id="element-568" data-mp="2a2arvaeyx5eb">
              Start with an empty database — exactly as it should be
            </h2>
            <p className="mt-2 text-sm text-muted-foreground" data-id="element-569" data-mp="1hc2j296bnxce">
              No seeded samples, no placeholders. The first record in your dashboard will be one you typed yourself.
            </p>
          </div>
          {user ? <button type="button" onClick={goNext} className="inline-flex h-12 shrink-0 items-center gap-2 rounded-2xl brand-gradient px-7 text-sm font-semibold text-white shadow-xl shadow-indigo-500/30" data-id="element-570" data-mp="1mb5dz0sl8n6n">
              Open dashboard
              <ArrowRight className="h-4 w-4" data-id="element-571" data-mp="w0v7yq6fp517" />
            </button> : <GoogleSignInButton className="shrink-0" onSignedIn={() => navigate('/security')} data-id="element-572" data-mp="vys9riv4o5le" />}
        </motion.section>
      </main>

      <footer className="mx-auto w-full max-w-6xl px-5 pb-10 text-center text-xs text-muted-foreground" data-id="element-573" data-mp="151b8bqrvrw1u">
        <p data-id="element-574" data-mp="19sjlmxwc01ka">
          Student Recorder — crafted by <span className="font-medium text-foreground" data-id="element-575" data-mp="19ucqc7ndwbpn">Paulin Dev Studio</span>. Powered
          by Firebase Authentication, Cloud Firestore and Firebase Storage.
        </p>
      </footer>
    </div>;
}